export interface Credentials {
  email: string;
  password: string;
}

export interface ClientData {
  id: string;
  name: string;
  pretty_name: string;
  cnpj: string;
  creation_time: string;
}

export interface Division {
  id: string;
  name: string;
  client_id: string;
  creator_id: string;
  users_count: number;
}

export interface User {
  client?: ClientData;
  client_id: string;
  id: string;
  name: string;
  email: string;
  roles: Role[];
  division?: Division;
}

export interface TenantOptionsData {
  enabled_features: string[];
  disabled_features: string[];
}

export interface AuthUser extends User {
  token: string;
  options: Record<string, unknown>;
  tenant_options?: TenantOptionsData;
}

// MFA types

export interface MfaRequiredResponse {
  requiresMfa: true;
  expiresAt: string;
  codeLength: number;
  locked?: boolean;
  exhausted?: boolean;
  retryAfterSeconds?: number;
}

export type LoginResponse = AuthUser | MfaRequiredResponse;

export function isMfaRequired(response: LoginResponse): response is MfaRequiredResponse {
  return 'requiresMfa' in response;
}

export interface MfaVerifyRequest {
  code: string;
  trustDevice?: boolean;
}

export interface MfaResendResponse {
  expiresAt: string;
  codeLength: number;
}

export type MfaErrorCode =
  | 'INVALID_CODE'
  | 'SESSION_LOCKED'
  | 'MAX_ATTEMPTS_EXCEEDED'
  | 'SESSION_EXPIRED'
  | 'INVALID_SESSION'
  | 'MISSING_SESSION'
  | 'RESEND_COOLDOWN';

export interface MfaErrorResponse {
  error: MfaErrorCode;
  attemptsRemaining?: number;
  retryAfterSeconds?: number;
}

export interface MfaSessionState {
  email: string;
  expiresAt: string;
  codeLength: number;
  locked?: boolean;
  exhausted?: boolean;
  retryAfterSeconds?: number;
  codeSentAt: number;
  resendCooldownEndsAt: number;
}

export const MFA_RESEND_COOLDOWN_MS = 60_000;

export enum Role {
  ADMIN = 'ADMIN',
  OPERATIONS_MANAGER = 'OPERATIONS_MANAGER',
  FINANCIAL_ANALYST = 'FINANCIAL_ANALYST',
  REVIEWER = 'REVIEWER',
  MANAGER = 'MANAGER',
  ANALYST = 'ANALYST',
  OPERATION_REQUESTER = 'OPERATION_REQUESTER',
  OPERATION_REGISTER = 'OPERATION_REGISTER',
  SALESMAN = 'SALESMAN',
  SALES_ADMIN = 'SALES_ADMIN',
  CREDIT_ANALYST = 'CREDIT_ANALYST',
  CREDIT_MANAGER = 'CREDIT_MANAGER',
  CARTOGRAPHY_REVIEWER = 'CARTOGRAPHY_REVIEWER',
  CARTOGRAPHY_ANALYST = 'CARTOGRAPHY_ANALYST',
  CUSTOMER_SUCCESS = 'CUSTOMER_SUCCESS',
  CUSTOMER_SUCCESS_REGISTER = 'CUSTOMER_SUCCESS_REGISTER',
  CREDIT_RISK_ANALYST = 'CREDIT_RISK_ANALYST',
  CREDIT_LEGAL_RISK_ANALYST = 'CREDIT_LEGAL_RISK_ANALYST',
  CREDITOR_RISK_ANALYST = 'CREDITOR_RISK_ANALYST',
  CREDIT_RISK_ANALYSIS_REQUESTER = 'CREDIT_RISK_ANALYSIS_REQUESTER',
  BUSINESS_GROUPS_MANAGER = 'BUSINESS_GROUPS_MANAGER',
  CREDIT_ENTITLEMENTS_DESPATCHER = 'CREDIT_ENTITLEMENTS_DESPATCHER',
  CREDIT_ENTITLEMENTS_MANAGER = 'CREDIT_ENTITLEMENTS_MANAGER',
  CREDIT_PORTFOLIOS_VIEWER = 'CREDIT_PORTFOLIOS_VIEWER',
  CREDIT_PORTFOLIOS_APPROVER = 'CREDIT_PORTFOLIOS_APPROVER',
  CREDIT_PORTFOLIOS_MANAGER = 'CREDIT_PORTFOLIOS_MANAGER',
  SERPRO_VIEWER = 'SERPRO_VIEWER',
  SERPRO_SUBMITTER = 'SERPRO_SUBMITTER',
  PROFILE_VIEWER = 'PROFILE_VIEWER',
  PROFILE_MANAGER = 'PROFILE_MANAGER',
  PARTNER_ACE = 'PARTNER_ACE',
  PARTNER_MANAGEMENT = 'PARTNER_MANAGEMENT',
  DISBURSEMENT_OPERATION_VIEWER = 'DISBURSEMENT_OPERATION_VIEWER',
  DISBURSEMENT_OPERATION_MANAGER = 'DISBURSEMENT_OPERATION_MANAGER',
  FARMER_ANALYSIS_MANAGER = 'FARMER_ANALYSIS_MANAGER',
  SALES_REPRESENTATIVE = 'SALES_REPRESENTATIVE',
  COMMERCIAL_MANAGER = 'COMMERCIAL_MANAGER',
  REGISTRATION_AGENT = 'REGISTRATION_AGENT',
  DEBT_MANAGER = 'DEBT_MANAGER',
  DEBT_ANALYST = 'DEBT_ANALYST',
  DEBT_NEGATIVATION_MANAGER = 'DEBT_NEGATIVATION_MANAGER',
  COLLECTION_WORKFLOW_MANAGER = 'COLLECTION_WORKFLOW_MANAGER',
  COLLECTION_WORKFLOW_VIEWER = 'COLLECTION_WORKFLOW_VIEWER',
  CREDIT_LIMIT_APPROVER = 'CREDIT_LIMIT_APPROVER',
  CREDIT_LIMIT_MANAGER = 'CREDIT_LIMIT_MANAGER',
  CREDIT_LIMIT_VIEWER = 'CREDIT_LIMIT_VIEWER',
  FORMALIZATION_AGENT = 'FORMALIZATION_AGENT',
}
